import React, { Component } from 'react';
import { AppProvider } from '@shopify/polaris';
import Cookies from "js-cookie";
import translations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';
import './styles.scss';
import AppFrame from './AppFrame';
import { Link as ReactRouterLink } from 'react-router-dom';
import { apiFetch as fetch } from './common/Helpers';
import { withRouter } from 'react-router';
import { AppContext, DefaultAppState } from './common/AppContext';
import { AppSettings } from './common/AppSettings';

const IS_EXTERNAL_LINK_REGEX = /^(?:[a-z][a-z\d+.-]*:|\/\/)/;

function Link({ children, url = '', external, ref, ...rest }) {
  // react-router only supports links to pages it can handle itself. It does not
  // support arbirary links, so anything that is not a path-based link should
  // use a reglar old `a` tag
  if (external || IS_EXTERNAL_LINK_REGEX.test(url)) {
    rest.target = '_blank';
    rest.rel = 'noopener noreferrer';
    return (
      <a href={url} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <ReactRouterLink to={url} {...rest}>
      {children}
    </ReactRouterLink>
  );
}

class App extends Component {

  static displayName = App.name;
  loginSent = false;

  constructor(props) {
    super(props);

    this.state = {
      appState: {
        ...DefaultAppState,
        requestChangePermissions: this.onChangePermissions,
        requestAdditionalPermissions: this.onRequestAdditionalPermissions,
        requestPermissions: this.onRequestCustomerPermissions,
        requestOrderPermissions: this.onRequestOrderPermissions,
        updateInitState: this.updateInitState,
        toggleNavigation: this.handleToggleNavigation,
        onCompleteFeedbackSurvey: this.handleCompleteFeedbackSurvey
      }
    };

    if (window.clarity != null) {
      var o1 = Cookies.get("shopOrigin");
      window.clarity("identify", o1);
      window.clarity("set", "domain", o1);
    }
  }

  render() {

    const shopOrigin = Cookies.get("shopOrigin");
    const shopKey = Cookies.get("__genie");

    return (
      <AppProvider
        linkComponent={Link}
        i18n={translations}>
        <AppContext.Provider value={this.state.appState}>
          <AppFrame
            shopOrigin={shopOrigin}
            shopKey={shopKey}
            isInit={this.state.appState.isInit}
            showValidationNavigation={this.state.appState.showValidationNavigation}
            isDev={this.state.appState.isDev}
            cohorts={this.state.appState.cohorts}
            enabledFeatures={this.state.appState.enabledFeatures}
            isPrePlanChange={this.state.appState.isPrePlanChange}
            isSubscribed={this.state.appState.isSubscribed}
            shopifyPlan={this.state.appState.shopifyPlan}
            completedSurvey={this.state.appState.completedSurvey}
            isLoading={this.state.appState.isLoading}
            isSuspended={this.state.appState.isSuspended}
            email={this.state.appState.email}
            onInit={this.handleOnInit}
            onUnsuspend={this.handleOnUnsuspend}
            onCompleteSurvey={this.handleOnCompleteSurvey} />
        </AppContext.Provider>
      </AppProvider>
    );
  }

  onRequestCustomerPermissions = () => {
    const shopOrigin = Cookies.get("shopOrigin");
    const apiKey = AppSettings.ShopifyApiKey;
    const redirectUri = AppSettings.AuthRedirect;
    const newScopes = this.state.appState.scopes.join(",") + ",write_customers,read_customers";
    const permissionUrl = `https://${shopOrigin}/admin/oauth/authorize?client_id=${apiKey}&scope=${newScopes}&access_mode=offline&redirect_uri=${redirectUri}&nonce=4257d4b5-d65d-43fa-8c16-56df3c53e60b`;

    window.location.assign(permissionUrl);
  }

  onRequestOrderPermissions = () => {
    const shopOrigin = Cookies.get("shopOrigin");
    const apiKey = AppSettings.ShopifyApiKey;
    const redirectUri = AppSettings.AuthRedirect;
    const newScopes = this.state.appState.scopes.join(",") + ",write_orders,read_all_orders";
    const permissionUrl = `https://${shopOrigin}/admin/oauth/authorize?client_id=${apiKey}&scope=${newScopes}&access_mode=offline&redirect_uri=${redirectUri}&nonce=4257d4b5-d65d-43fa-8c16-56df3c53e60b`;

    window.location.assign(permissionUrl);
  }

  onChangePermissions = (products, customers, orders, fulfillmentOrders) => {
    var permissions = [""];

    if (products) {
      permissions.push("write_products");
    }

    if (customers) {
      permissions.push("write_customers");
    }

    if (orders) {
      permissions.push("write_orders");
      // permissions.push("read_all_orders");
    }

    if (fulfillmentOrders) {
      permissions.push("read_assigned_fulfillment_orders");
      permissions.push("read_merchant_managed_fulfillment_orders");
      permissions.push("read_third_party_fulfillment_orders");
    }

    const shopOrigin = Cookies.get("shopOrigin");
    const apiKey = AppSettings.ShopifyApiKey;
    const redirectUri = AppSettings.AuthRedirect;
    const newScopes = permissions.join(",");
    const permissionUrl = `https://${shopOrigin}/admin/oauth/authorize?client_id=${apiKey}&scope=${newScopes}&access_mode=offline&redirect_uri=${redirectUri}&nonce=4257d4b5-d65d-43fa-8c16-56df3c53e60b`;

    window.location.assign(permissionUrl);
  }

  onRequestAdditionalPermissions = (newPermissions) => {
    var scopes = this.state.appState.scopes;

    if (newPermissions.includes("products")) {
      scopes.push("write_products");
    }

    if (newPermissions.includes("customers")) {
      scopes.push("write_customers");
    }

    if (newPermissions.includes("fulfillmentOrders")) {
      scopes.push("read_assigned_fulfillment_orders");
      scopes.push("read_merchant_managed_fulfillment_orders");
      scopes.push("read_third_party_fulfillment_orders");
    }

    if (newPermissions.includes("orders")) {
      scopes.push("write_orders");
      // scopes.push("read_all_orders");
    }

    const shopOrigin = Cookies.get("shopOrigin");
    const apiKey = AppSettings.ShopifyApiKey;
    const redirectUri = AppSettings.AuthRedirect;
    const newScopes = [...new Set(scopes)].join(",");
    const permissionUrl = `https://${shopOrigin}/admin/oauth/authorize?client_id=${apiKey}&scope=${newScopes}&access_mode=offline&redirect_uri=${redirectUri}&nonce=4257d4b5-d65d-43fa-8c16-56df3c53e60b`;

    window.location.assign(permissionUrl);
  }

  updateInitState = async (isInit, requiredData) => {
    this.setState({
      appState: {
        ...this.state.appState,
        isInit: isInit,
        requiredData: requiredData
      }
    });
  }

  handleToggleNavigation = (showNavigation) => {
    this.setState({
      appState: {
        ...this.state.appState,
        hideNavigation: showNavigation != null ? showNavigation : !this.state.appState.hideNavigation
      }
    });
  }

  handleCompleteFeedbackSurvey = () => {
    this.setState({
      appState: {
        ...this.state.appState,
        shouldCompleteFeedbackSurvey: false
      }
    });
  }

  handleOnInit = () => {
    this.setState({
      appState: {
        ...this.state.appState,
        isInit: true
      }
    });
  }

  handleOnUnsuspend = () => {
    this.setState({
      appState: {
        ...this.state.appState,
        isSuspended: false,
        isInit: false
      }
    });
  }

  handleOnCompleteSurvey = () => {
    this.setState({
      appState: {
        ...this.state.appState,
        completedSurvey: true
      }
    });
  }

  getConnectionInfo = () => {

    const shopOrigin = Cookies.get("shopOrigin");

    fetch('api/storeConnections/info')
      .then(response => { return response.json() })
      .then(data => {
        this.setState({
          appState: {
            ...this.state.appState,
            isInit: data.isInit,
            requiredData: data.requiredData,
            isSuspended: data.isSuspended,
            completedSurvey: data.completedSurvey,
            isSubscribed: data.isSubscribed,
            isDev: data.isDev,
            isLoading: false,
            shopifyPlan: data.shopifyPlan,
            isPrePlanChange: data.isPrePlanChange,
            email: data.email,
            tierName: data.tierName,
            productCount: data.productCount,
            cohorts: data.cohorts,
            scopes: data.scopes,
            enabledFeatures: data.enabledFeatures,
            showValidationNavigation: data.showValidationNavigation,
            hasCustomerPermissions: data.hasCustomerPermissions,
            hasOrderPermissions: data.hasOrderPermissions,
            hasFulfillmentOrderPermissions: data.hasFulfillmentOrderPermissions,
            syncType: data.syncType,
            shouldCompleteFeedbackSurvey: data.shouldCompleteFeedbackSurvey,
          }
        });

        if (window.$crisp != null) {
          window.CRISP_TOKEN_ID = data.id;
          window.$crisp.push(["do", "session:reset"]);
          window.$crisp.push(["set", "session:data", [[["tierName", data.tierName]]]]);
          window.$crisp.push(["set", "user:nickname", [shopOrigin]]);
          window.$crisp.push(["set", "user:company", [shopOrigin]]);
        }
      })
      .then(() => {

      });
  }

  componentDidMount = async () => {

    const disableChat = Cookies.get("disableChat");

    if (disableChat !== "yes") {
      if (window.location.hostname === "app.taggenieapp.com") {
        window.$crisp = [];
        window.CRISP_WEBSITE_ID = "710e5274-c633-40c3-b192-722fc826c29f";

        (function () {
          var d = document;
          var s = d.createElement("script");

          s.src = "https://client.crisp.chat/l.js";
          s.async = 1;
          d.getElementsByTagName("head")[0].appendChild(s);
        })();
      }
    }

    if (!this.loginSent) {
      await fetch('api/storeConnections/login', { 'method': "POST" });
    }

    this.getConnectionInfo();
  }
}

export default withRouter(App);
